import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;
const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;
const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;
const skeletonPulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50%      { opacity: 0.3; }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;
const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;
const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em 2em 2em;
  position: relative;
  min-height: 100vh;
  overflow-y: auto;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  /* ========== HEADER ========== */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 16px;
    flex-shrink: 0;

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "FORNECEDORES · DASHBOARD";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${tokens.brand};

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandRing};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }

          &.error {
            color: ${tokens.danger};
            &::before { background: ${tokens.danger}; box-shadow: 0 0 8px rgba(239,68,68,0.28); }
          }
        }

        .update-tag {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          ${mono};
          font-size: 0.9em;
          &::before { content: "· "; opacity: 0.5; }
        }

        .refresh-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 5px;
          background: transparent;
          border: none;
          border-radius: ${tokens.radiusSm};
          cursor: pointer;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          transition: all ${tokens.durBase} ${tokens.easeOut};

          &:hover { opacity: 1; background: ${({ theme }) => surface(theme, 1)}; }
          &:disabled { opacity: 0.25; cursor: not-allowed; }
          svg { font-size: 0.85em; }
        }
      }
    }
  }

  .spin { animation: ${spin} 1s linear infinite; }
  .skeleton-loading { animation: ${skeletonPulse} 1.5s ease-in-out infinite; opacity: 0.6; }

  /* ========== TABS HEADER ========== */
  .tabs-header {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
    flex-shrink: 0;

    .tab {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      background: transparent;
      border: 1px solid transparent;
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      ${mono};
      text-transform: uppercase;
      letter-spacing: 0.04em;

      &:hover { opacity: 0.85; background: ${({ theme }) => surface(theme, 1)}; }

      &.active {
        opacity: 1;
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
      }

      svg { font-size: 0.95em; }
    }
  }

  /* ========== KPI CARDS ========== */
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;

    &.main-stats { margin-bottom: 12px; }
    &.tax-stats { grid-template-columns: repeat(6, 1fr); }
    &.secondary-stats { grid-template-columns: repeat(5, 1fr); }

    @media (max-width: 1200px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 500px)  { grid-template-columns: 1fr; }
  }

  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .card-content {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .thumb {
        opacity: 0.35;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        display: inline-flex;
        align-items: center;
      }

      .text {
        display: flex;
        flex-direction: column;
        gap: 2px;

        p { margin: 0; }

        p:first-child {
          ${mono};
          font-size: 1.6em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        p:last-child {
          font-size: 0.72em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          margin-top: 2px;
          letter-spacing: -0.005em;
        }
      }
    }

    &.success .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
    &.warning .card-content .thumb { color: ${tokens.info};  opacity: 0.65; }
    &.danger  .card-content .thumb { color: ${tokens.danger}; opacity: 0.65; }
    &.info    .card-content .thumb { color: ${tokens.warn};  opacity: 0.65; }
    &.primary .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
    &.cte     .card-content .thumb { color: ${tokens.info};  opacity: 0.65; }
    &.nfe     .card-content .thumb { color: ${tokens.warn};  opacity: 0.65; }

    &.compact {
      min-height: 72px;
      padding: 12px 14px;
      .text p:first-child { font-size: 1.35em; }
      .text p:last-child { font-size: 0.68em; }
    }
  }

  /* ========== FILTERS ========== */
  .filters-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;
    flex-wrap: wrap;

    .search-box {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 7px 12px;
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusMd};
      flex: 1;
      max-width: 360px;
      transition: border-color ${tokens.durBase} ${tokens.easeOut};

      &:focus-within { border-color: ${tokens.brandRing}; }

      svg {
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        font-size: 0.9em;
        flex-shrink: 0;
      }

      input {
        background: transparent;
        border: none;
        outline: none;
        color: ${({ theme }) => theme.text};
        font-family: ${tokens.fontSans};
        font-size: 0.82em;
        flex: 1;
        letter-spacing: -0.005em;

        &::placeholder { opacity: 0.4; }
      }
    }

    @media (max-width: 900px) {
      .search-box { max-width: none; }
    }
  }

  /* ========== COMPACT CATEGORIES CARD ========== */
  .compact-categories {
    max-width: 100%;
    background: ${({ theme }) => surface(theme, 0)};
    border-radius: ${tokens.radiusLg};
    padding: 10px;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 0;

    .donut-wrapper {
      flex-direction: column;
      gap: 0;
      align-items: center;
      justify-content: flex-start;
      height: 100%;

      .donut-header {
        width: 100%;
        text-align: left;
        margin-bottom: 8px;
        flex-shrink: 0;

        h4 {
          margin: 0 0 3px 0;
          font-size: 0.82em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.01em;
        }

        p {
          margin: 0;
          font-size: 0.68em;
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          font-weight: 400;
        }
      }

      .donut-chart {
        width: 200px;
        height: 200px;
        flex-shrink: 0;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 10px;

        .donut-center {
          position: absolute;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);

          .donut-total {
            font-size: 1.5em;
            font-weight: 700;
            color: #fff;
            letter-spacing: -0.02em;
            line-height: 1;
          }

          .donut-label {
            font-size: 0.6em;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            margin-top: 3px;
            text-transform: uppercase;
            letter-spacing: 0.06em;
          }
        }
      }

      .categories-list {
        width: 100%;
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding-right: 6px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 6px;

        &::-webkit-scrollbar { width: 3px; }
        &::-webkit-scrollbar-thumb {
          background: ${({ theme }) => hairline(theme, true)};
          border-radius: 2px;
        }

        .category-item {
          padding: 7px 10px;
          background: ${({ theme }) => surface(theme, 1)};
          border-radius: ${tokens.radiusSm};
          border: 1px solid ${({ theme }) => hairline(theme)};
          transition: border-color ${tokens.durBase} ${tokens.easeOut},
                      opacity ${tokens.durBase} ${tokens.easeOut};
          cursor: default;
          opacity: 1;

          &:hover {
            border-color: ${({ theme }) => hairline(theme, true)};
          }

          .category-header {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 5px;

            .category-color {
              width: 10px;
              height: 10px;
              border-radius: 3px;
              flex-shrink: 0;
            }

            .category-name {
              flex: 1;
              font-size: 0.72em;
              font-weight: 500;
              color: ${({ theme }) => theme.text};
              letter-spacing: -0.005em;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .category-count {
              font-size: 0.65em;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              ${mono};
              opacity: 0.6;
            }
          }

          .category-bar {
            width: 100%;
            height: 3px;
            background: ${({ theme }) => hairline(theme)};
            border-radius: 2px;
            overflow: hidden;
            margin-bottom: 4px;
            box-sizing: border-box;

            .category-progress {
              height: 100%;
              border-radius: 2px;
              transition: width 0.3s ease;
            }
          }

          .category-value {
            font-size: 0.68em;
            ${mono};
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            text-align: right;
          }
        }
      }
    }
  }

  /* ========== STATS TAB ========== */
  .stats-tab {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 20px 0;
    width: 100%;
    align-self: stretch;

    /* KPIs Estratégicos Grid */
    .stats-kpi-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;

      @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
      }

      @media (max-width: 600px) {
        grid-template-columns: 1fr;
      }
    }

    .stats-kpi-card {
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;
      padding: 20px;
      transition: all 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      .kpi-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        .kpi-icon {
          font-size: 20px;
          opacity: 0.7;
        }

        .kpi-badge {
          font-size: 10px;
          font-weight: 600;
          padding: 4px 8px;
          border-radius: 6px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }

      .kpi-value {
        font-size: 28px;
        font-weight: 700;
        color: ${({ theme }) => theme.text};
        margin-bottom: 4px;
        font-family: 'Inter', system-ui, sans-serif;
      }

      .kpi-label {
        font-size: 12px;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        margin-bottom: 12px;
      }

      .kpi-trend {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        font-weight: 500;

        svg {
          font-size: 12px;
        }

        &.positive {
          color: #10b981;
        }

        &.negative {
          color: #ef4444;
        }

        &.neutral {
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
        }
      }

      &.primary {
        border-left: 3px solid #3b82f6;
        .kpi-icon {
          color: #3b82f6;
        }
        .kpi-badge {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }
      }

      &.success {
        border-left: 3px solid #10b981;
        .kpi-icon {
          color: #10b981;
        }
        .kpi-badge {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }
      }

      &.warning {
        border-left: 3px solid #f59e0b;
        .kpi-icon {
          color: #f59e0b;
        }
        .kpi-badge {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }
      }

      &.info {
        border-left: 3px solid #8b5cf6;
        .kpi-icon {
          color: #8b5cf6;
        }
        .kpi-badge {
          background: rgba(139, 92, 246, 0.1);
          color: #8b5cf6;
        }
      }
    }

    /* Stats Row */
    .stats-row {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 24px;

      @media (max-width: 1200px) {
        grid-template-columns: 1fr;
      }

      &.three-cols {
        grid-template-columns: 1fr 1fr 1fr;

        @media (max-width: 1400px) {
          grid-template-columns: 1fr 1fr;

          .stats-card.concentration {
            grid-column: 1 / -1;
          }
        }

        @media (max-width: 900px) {
          grid-template-columns: 1fr;
        }
      }
    }

    /* Top Estados Card */
    .stats-card.top-estados {
      .estados-list {
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-height: 380px;
        overflow-y: auto;

        &::-webkit-scrollbar {
          width: 4px;
        }
        &::-webkit-scrollbar-thumb {
          background: ${({ theme }) => theme.border};
          border-radius: 2px;
        }

        .estado-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          background: ${({ theme }) => theme.tableRow};
          border-radius: 12px;
          border-left: 3px solid;
          transition: all 0.2s ease;
          cursor: pointer;

          &:hover {
            transform: translateX(4px);
            background: ${({ theme }) => theme.tableRowHover};
          }

          .estado-rank {
            width: 28px;
            height: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 700;
            flex-shrink: 0;
          }

          .estado-info {
            flex: 1;
            min-width: 0;

            .estado-header {
              display: flex;
              align-items: center;
              gap: 6px;
              margin-bottom: 2px;

              .estado-uf {
                font-size: 14px;
                font-weight: 700;
                color: ${({ theme }) => theme.text};
              }

              .estado-separator {
                font-size: 12px;
                color: #64748b;
              }

              .estado-nome {
                font-size: 13px;
                font-weight: 500;
                color: #94a3b8;
              }
            }

            .estado-meta {
              display: flex;
              align-items: center;
              gap: 8px;

              .estado-regiao {
                font-size: 10px;
                color: #64748b;
                background: rgba(100, 116, 139, 0.1);
                padding: 2px 6px;
                border-radius: 6px;
              }

              .estado-count {
                font-size: 10px;
                color: #64748b;
              }
            }
          }

          .estado-valores {
            text-align: right;
            flex-shrink: 0;

            .estado-valor {
              display: block;
              font-size: 14px;
              font-weight: 700;
              margin-bottom: 4px;
            }

            .estado-percent {
              font-size: 10px;
              padding: 2px 8px;
              border-radius: 12px;
              font-weight: 600;
            }
          }
        }
      }
    }

    /* Mapa Brasil Card */
    .stats-card.mapa-brasil {
      .mapa-container {
        padding: 16px;
        display: flex;
        flex-direction: column;
        align-items: center;

        .brazil-map {
          width: 100%;
          max-width: 320px;
          height: auto;

          path {
            transition: all 0.2s ease;
            cursor: pointer;

            &:hover {
              opacity: 0.8;
              transform: scale(1.02);
            }
          }

          text {
            pointer-events: none;
            font-weight: 600;
          }
        }

        .mapa-legenda {
          display: flex;
          justify-content: center;
          gap: 16px;
          margin-top: 16px;
          flex-wrap: wrap;

          .legenda-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 11px;
            color: ${({ theme }) => theme.text};
            opacity: 0.7;

            .legenda-cor {
              width: 12px;
              height: 12px;
              border-radius: 3px;
            }
          }
        }
      }
    }

    /* Stats Card */
    .stats-card {
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;
      overflow: hidden;

      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        border-bottom: 1px solid ${({ theme }) => theme.border};

        .header-title {
          display: flex;
          align-items: center;
          gap: 10px;

          svg {
            font-size: 16px;
            color: ${({ theme }) => theme.primary};
            opacity: 0.8;
          }

          h4 {
            margin: 0;
            font-size: 14px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
          }
        }

        .header-badge {
          font-size: 10px;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 6px;
          background: ${({ theme }) => theme.buttonBg};
          color: ${({ theme }) => theme.text};
          opacity: 0.7;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .chart-legend {
          display: flex;
          gap: 16px;

          .legend-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 11px;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;

            .legend-color {
              width: 10px;
              height: 10px;
              border-radius: 2px;
            }
          }
        }
      }

      &.credits-analysis {
        grid-column: 1 / -1;
      }

      &.evolution-chart {
        .chart-container {
          padding: 20px;
        }
      }
    }

    /* ABC Analysis */
    .abc-analysis {
      padding: 20px;

      .abc-chart {
        margin-bottom: 24px;

        .abc-bar-container {
          display: flex;
          height: 40px;
          border-radius: 12px;
          overflow: hidden;
          margin-bottom: 12px;

          .abc-bar {
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;

            .abc-label {
              font-size: 14px;
              font-weight: 700;
              color: #fff;
            }

            &.class-a {
              background: linear-gradient(135deg, #10b981, #059669);
            }

            &.class-b {
              background: linear-gradient(135deg, #3b82f6, #2563eb);
            }

            &.class-c {
              background: linear-gradient(135deg, #8b5cf6, #7c3aed);
            }
          }
        }

        .abc-values {
          display: flex;

          .abc-value {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 8px;

            &.class-a {
              width: 20%;
            }
            &.class-b {
              width: 30%;
            }
            &.class-c {
              width: 50%;
            }

            .value {
              font-size: 16px;
              font-weight: 700;
              color: ${({ theme }) => theme.text};
            }

            .label {
              font-size: 10px;
              color: ${({ theme }) => theme.text};
              opacity: 0.5;
            }
          }
        }
      }

      .abc-details {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .abc-item {
          padding: 14px 16px;
          border-radius: 12px;
          border-left: 4px solid;

          &.class-a {
            background: rgba(16, 185, 129, 0.05);
            border-left-color: #10b981;
          }

          &.class-b {
            background: rgba(59, 130, 246, 0.05);
            border-left-color: #3b82f6;
          }

          &.class-c {
            background: rgba(139, 92, 246, 0.05);
            border-left-color: #8b5cf6;
          }

          .item-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 6px;

            .class-badge {
              font-size: 11px;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
            }

            .count {
              font-size: 10px;
              color: ${({ theme }) => theme.text};
              opacity: 0.5;
            }
          }

          .item-value {
            font-size: 18px;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
            margin-bottom: 4px;
            font-family: 'Inter', system-ui, sans-serif;
          }

          .item-desc {
            font-size: 11px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }
        }
      }
    }

    /* UF Distribution */
    .uf-distribution {
      padding: 20px;
      display: flex;
      flex-direction: column;
      gap: 16px;

      .uf-item {
        .uf-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 6px;

          .uf-name {
            font-size: 13px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
          }

          .uf-count {
            font-size: 11px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }
        }

        .uf-bar-container {
          height: 8px;
          background: ${({ theme }) => theme.buttonBg};
          border-radius: 6px;
          overflow: hidden;
          margin-bottom: 4px;

          .uf-bar {
            height: 100%;
            border-radius: 6px;
            transition: width 0.3s ease;
          }
        }

        .uf-value {
          font-size: 12px;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          font-family: 'Inter', system-ui, sans-serif;
        }
      }
    }

    /* Credits Grid */
    .credits-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 16px;
      padding: 20px;

      @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
      }

      @media (max-width: 600px) {
        grid-template-columns: 1fr;
      }

      .credit-card {
        padding: 16px;
        border-radius: 12px;
        border: 1px solid ${({ theme }) => theme.border};
        background: ${({ theme }) => theme.tableRow};

        .credit-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;

          .credit-type {
            font-size: 14px;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
          }

          .credit-cfop {
            font-size: 9px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            font-family: monospace;
          }
        }

        .credit-value {
          font-size: 22px;
          font-weight: 700;
          color: ${({ theme }) => theme.text};
          margin-bottom: 12px;
          font-family: 'Inter', system-ui, sans-serif;
        }

        .credit-details {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 12px;

          .detail-item {
            display: flex;
            justify-content: space-between;

            .detail-label {
              font-size: 11px;
              color: ${({ theme }) => theme.text};
              opacity: 0.6;
            }

            .detail-value {
              font-size: 11px;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              font-family: monospace;
            }
          }
        }

        .credit-bar {
          height: 6px;
          background: ${({ theme }) => theme.buttonBg};
          border-radius: 3px;
          overflow: hidden;
          margin-bottom: 6px;

          .bar-fill {
            height: 100%;
            border-radius: 3px;
          }
        }

        .credit-percent {
          font-size: 10px;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
        }

        &.icms {
          border-top: 3px solid #10b981;
          .credit-bar .bar-fill {
            background: #10b981;
          }
        }

        &.pis {
          border-top: 3px solid #ef4444;
          .credit-bar .bar-fill {
            background: #ef4444;
          }
        }

        &.cofins {
          border-top: 3px solid #f59e0b;
          .credit-bar .bar-fill {
            background: #f59e0b;
          }
        }

        &.ipi {
          border-top: 3px solid #3b82f6;
          .credit-bar .bar-fill {
            background: #3b82f6;
          }
        }
      }
    }

    /* Top Suppliers List */
    .top-list {
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;

      .top-item {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 14px 16px;
        background: ${({ theme }) => theme.tableRow};
        border-radius: 12px;
        transition: all 0.2s ease;

        &:hover {
          background: ${({ theme }) => theme.tableRowHover};
        }

        .rank-badge {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${({ theme }) => theme.primary};
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          border-radius: 12px;
        }

        .supplier-info {
          flex: 1;
          min-width: 0;

          .supplier-name {
            font-size: 13px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            margin-bottom: 4px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .supplier-meta {
            display: flex;
            gap: 12px;

            .cnpj,
            .category {
              font-size: 10px;
              color: ${({ theme }) => theme.text};
              opacity: 0.5;
            }

            .category {
              padding: 2px 6px;
              background: ${({ theme }) => theme.buttonBg};
              border-radius: 6px;
            }
          }
        }

        .supplier-stats {
          text-align: right;
          min-width: 120px;

          .stat-value {
            font-size: 14px;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
            margin-bottom: 6px;
            font-family: 'Inter', system-ui, sans-serif;
          }

          .stat-bar {
            height: 4px;
            background: ${({ theme }) => theme.buttonBg};
            border-radius: 2px;
            overflow: hidden;
            margin-bottom: 4px;

            .bar-fill {
              height: 100%;
              border-radius: 2px;
            }
          }

          .stat-percent {
            font-size: 10px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }
        }
      }
    }

    /* NCM List */
    .ncm-list {
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 10px;

      .ncm-item {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 12px 14px;
        background: ${({ theme }) => theme.tableRow};
        border-radius: 12px;

        .ncm-rank {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${({ theme }) => theme.buttonBg};
          color: ${({ theme }) => theme.text};
          font-size: 11px;
          font-weight: 600;
          border-radius: 6px;
          opacity: 0.7;
        }

        .ncm-info {
          flex: 1;
          min-width: 0;

          .ncm-code {
            font-size: 12px;
            font-weight: 600;
            color: ${({ theme }) => theme.primary};
            font-family: monospace;
            margin-bottom: 2px;
          }

          .ncm-desc {
            font-size: 11px;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .ncm-stats {
          text-align: right;

          .ncm-value {
            font-size: 13px;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
            font-family: 'Inter', system-ui, sans-serif;
          }

          .ncm-aliq {
            font-size: 10px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }
        }
      }
    }

    /* NCM List Professional */
    .ncm-list-pro {
      padding: 12px 16px;
      display: flex;
      flex-direction: column;
      gap: 8px;

      .ncm-item-pro {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 12px 14px;
        background: ${({ theme }) => theme.tableRow};
        border-radius: 12px;
        transition: all 0.2s ease;

        &:hover {
          background: ${({ theme }) => theme.tableRowHover};
        }

        .ncm-rank-pro {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${({ theme }) => theme.buttonBg};
          color: ${({ theme }) => theme.text};
          font-size: 11px;
          font-weight: 600;
          border-radius: 6px;
          opacity: 0.7;
          flex-shrink: 0;
        }

        .ncm-main {
          flex: 1;
          min-width: 0;

          .ncm-info-pro {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 8px;

            .ncm-code-pro {
              font-size: 12px;
              font-weight: 600;
              color: ${({ theme }) => theme.primary};
              font-family: "JetBrains Mono", "Consolas", monospace;
              background: ${({ theme }) => theme.buttonBg};
              padding: 2px 8px;
              border-radius: 6px;
            }

            .ncm-desc-pro {
              font-size: 12px;
              color: ${({ theme }) => theme.text};
              opacity: 0.6;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }

          .ncm-bar-pro {
            height: 3px;
            background: ${({ theme }) => theme.buttonBg};
            border-radius: 2px;
            overflow: hidden;

            .bar-fill-pro {
              height: 100%;
              background: ${({ theme }) => theme.primary};
              border-radius: 2px;
              transition: width 0.5s ease;
              opacity: 0.7;
            }
          }
        }

        .ncm-values-pro {
          text-align: right;
          flex-shrink: 0;
          min-width: 100px;

          .ncm-amount {
            font-size: 14px;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
            font-family: 'Inter', system-ui, sans-serif;
            margin-bottom: 4px;
          }

          .ncm-details {
            display: flex;
            align-items: center;
            gap: 8px;
            justify-content: flex-end;

            .ncm-pct {
              font-size: 10px;
              color: ${({ theme }) => theme.text};
              opacity: 0.5;
            }

            .ncm-ipi {
              font-size: 9px;
              font-weight: 500;
              color: ${({ theme }) => theme.text};
              opacity: 0.5;
              background: ${({ theme }) => theme.buttonBg};
              padding: 2px 6px;
              border-radius: 6px;
            }
          }
        }
      }
    }

    /* Category Analysis */
    .category-list {
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      max-height: 340px;
      overflow-y: auto;

      .category-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 14px 16px;
        background: ${({ theme }) => theme.tableRow};
        border-radius: 12px;
        border-left: 3px solid;
        transition: all 0.2s ease;

        &:hover {
          transform: translateX(4px);
          background: ${({ theme }) => theme.tableRowHover};
        }

        .category-info {
          flex: 1;
          min-width: 0;

          .category-name {
            font-size: 14px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            margin-bottom: 4px;
          }

          .category-meta {
            font-size: 11px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }
        }

        .category-stats {
          text-align: right;
          min-width: 140px;

          .category-value {
            font-size: 15px;
            font-weight: 700;
            font-family: 'Inter', system-ui, sans-serif;
            margin-bottom: 6px;
          }

          .category-bar {
            height: 4px;
            background: ${({ theme }) => theme.buttonBg};
            border-radius: 2px;
            overflow: hidden;
            margin-bottom: 4px;

            .bar-fill {
              height: 100%;
              border-radius: 2px;
              transition: width 0.5s ease;
            }
          }

          .category-percent {
            font-size: 11px;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
          }
        }
      }
    }

    /* Risk Analysis */
    .risk-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      padding: 16px 20px;

      @media (max-width: 600px) {
        grid-template-columns: 1fr;
      }

      .risk-item {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 14px;
        border-radius: 12px;

        .risk-icon {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 700;
        }

        .risk-content {
          .risk-count {
            font-size: 24px;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
            line-height: 1;
            margin-bottom: 4px;
          }

          .risk-label {
            font-size: 12px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            margin-bottom: 4px;
          }

          .risk-desc {
            font-size: 10px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }
        }

        &.low {
          background: rgba(16, 185, 129, 0.08);
          .risk-icon {
            background: rgba(16, 185, 129, 0.15);
            color: #10b981;
          }
        }

        &.medium {
          background: rgba(245, 158, 11, 0.08);
          .risk-icon {
            background: rgba(245, 158, 11, 0.15);
            color: #f59e0b;
          }
        }

        &.high {
          background: rgba(239, 68, 68, 0.08);
          .risk-icon {
            background: rgba(239, 68, 68, 0.15);
            color: #ef4444;
          }
        }
      }
    }

    .risk-summary {
      display: flex;
      justify-content: space-around;
      padding: 16px 20px;
      border-top: 1px solid ${({ theme }) => theme.border};

      .summary-item {
        text-align: center;

        .summary-label {
          display: block;
          font-size: 10px;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          margin-bottom: 4px;
        }

        .summary-value {
          font-size: 14px;
          font-weight: 700;
          color: ${({ theme }) => theme.text};

          &.good {
            color: #10b981;
          }
        }
      }
    }

    /* Payment Metrics */
    .payment-metrics {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
      padding: 16px 20px;

      @media (max-width: 600px) {
        grid-template-columns: 1fr;
      }

      .metric-card {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 14px;
        background: ${({ theme }) => theme.tableRow};
        border-radius: 12px;

        .metric-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${({ theme }) => theme.buttonBg};
          border-radius: 12px;
          color: ${({ theme }) => theme.text};
          opacity: 0.6;

          svg {
            font-size: 18px;
          }
        }

        .metric-content {
          flex: 1;

          .metric-value {
            font-size: 16px;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
            margin-bottom: 2px;
          }

          .metric-label {
            font-size: 10px;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }
        }

        .metric-trend {
          font-size: 11px;
          font-weight: 600;
          padding: 4px 8px;
          border-radius: 6px;

          &.positive {
            background: rgba(16, 185, 129, 0.1);
            color: #10b981;
          }

          &.negative {
            background: rgba(239, 68, 68, 0.1);
            color: #ef4444;
          }

          &.neutral {
            background: ${({ theme }) => theme.buttonBg};
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
          }
        }
      }
    }

    .stats-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 40px;
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;

      svg {
        color: ${({ theme }) => theme.text};
        opacity: 0.3;
        margin-bottom: 16px;
      }

      h4 {
        margin: 0 0 8px 0;
        font-size: 16px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
      }

      p {
        margin: 0;
        font-size: 14px;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
      }
    }
  }

  /* ========== CATEGORIES CARD ========== */
  .categories-card {
    width: 100%;
    height: 100%;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    overflow: hidden;

    .card-header {
      padding: 16px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid ${({ theme }) => theme.border};

      h4 {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
      }

      .card-badge {
        font-size: 11px;
        padding: 4px 8px;
        background: ${({ theme }) => theme.background};
        border-radius: 6px;
        color: ${({ theme }) => theme.text};
        opacity: 0.7;
      }
    }

    .donut-chart {
      padding: 20px;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;

      svg {
        width: 140px;
        height: 140px;
      }

      .donut-center {
        position: absolute;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .donut-value {
          font-size: 24px;
          font-weight: 700;
          color: ${({ theme }) => theme.text};
        }

        .donut-label {
          font-size: 11px;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          text-transform: uppercase;
        }
      }
    }

    .categories-list {
      flex: 1;
      overflow-y: auto;
      padding: 0 4px 8px 4px;

      &::-webkit-scrollbar {
        width: 4px;
      }
      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.border};
        border-radius: 2px;
      }

      .category-item {
        padding: 12px 16px;
        border-bottom: 1px solid ${({ theme }) => theme.border};

        &:last-child {
          border-bottom: none;
        }

        .category-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;

          .category-color {
            width: 10px;
            height: 10px;
            border-radius: 50%;
          }

          .category-name {
            flex: 1;
            font-size: 12px;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
          }

          .category-count {
            font-size: 11px;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
            font-family: monospace;
          }
        }

        .category-details {
          display: flex;
          justify-content: space-between;
          margin-bottom: 6px;

          .category-percentage,
          .category-revenue {
            font-size: 10px;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
          }

          .category-revenue {
            font-family: monospace;
            font-weight: 600;
          }
        }

        .category-bar {
          width: 100%;
          height: 3px;
          background: transparent;
          border-radius: 2px;
          overflow: hidden;

          .category-progress {
            height: 100%;
            border-radius: 2px;
            transition: width 0.3s ease;
          }
        }
      }
    }
  }

  /* ========== MONTHLY CHART ========== */
  .monthly-chart {
    width: 100%;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    overflow: hidden;
    margin-top: 24px;

    .chart-header {
      padding: 16px 20px;
      display: flex;
      align-items: center;

      svg {
        margin-right: 8px;
        opacity: 0.7;
        color: ${({ theme }) => theme.text};
        font-size: 16px;
      }

      h4 {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
      }
    }

    .chart-content {
      display: grid;
      grid-template-columns: 280px 1fr;
      gap: 0;
      min-height: 320px;

      @media (max-width: 900px) {
        grid-template-columns: 1fr;
      }
    }

    .month-info-card {
      border-right: 1px solid ${({ theme }) => theme.border};
      padding: 20px;

      .month-info-header {
        margin-bottom: 20px;

        h5 {
          margin: 0 0 4px 0;
          font-size: 18px;
          font-weight: 700;
          color: ${({ theme }) => theme.text};
        }

        .month-index {
          font-size: 12px;
          color: ${({ theme }) => theme.text};
          opacity: 0.6;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }

      .month-metrics {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .metric-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px;
          background: ${({ theme }) => theme.card};
          border: 1px solid ${({ theme }) => theme.border};
          border-radius: 6px;

          &.novos {
            border-left: 4px solid #3b82f6;
          }
          &.ativos {
            border-left: 4px solid #10b981;
          }
          &.compras {
            border-left: 4px solid #f59e0b;
          }

          .metric-icon {
            font-size: 16px;
            margin-top: 2px;
            opacity: 0.7;
          }

          .metric-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 2px;

            .metric-label {
              font-size: 11px;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              opacity: 0.6;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }

            .metric-value {
              font-size: 16px;
              font-weight: 700;
              color: ${({ theme }) => theme.text};
              font-family: monospace;
            }

            .impostos-breakdown {
              display: flex;
              flex-direction: column;
              gap: 4px;
              margin-top: 8px;
              padding-top: 8px;
              border-top: 1px solid ${({ theme }) => theme.border};

              .imposto-item {
                display: flex;
                justify-content: space-between;
                align-items: center;

                .imposto-label {
                  font-size: 10px;
                  color: ${({ theme }) => theme.text};
                  opacity: 0.6;
                  text-transform: uppercase;
                }

                .imposto-value {
                  font-size: 11px;
                  font-weight: 600;
                  color: ${({ theme }) => theme.text};
                  font-family: monospace;
                }
              }
            }
          }
        }
      }
    }

    .chart-container {
      padding: 20px;

      * {
        outline: none !important;
      }

      *:focus {
        outline: none !important;
      }

      .chart-header-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        h4 {
          margin: 0;
          font-size: 0.95em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }

        .chart-legend {
          display: flex;
          gap: 16px;
          margin-bottom: 0;

          .legend-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 11px;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;

            .legend-color {
              width: 10px;
              height: 10px;
              border-radius: 2px;
            }
          }
        }
      }

      .chart-placeholder {
        height: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${({ theme }) => theme.contrast};
        border-radius: 12px;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        font-size: 14px;
      }

      /* Transição suave nas barras do gráfico */
      .recharts-bar-rectangle {
        transition: opacity 0.2s ease;
      }

      /* Remover borda do gráfico */
      .recharts-wrapper,
      .recharts-wrapper *,
      .recharts-surface,
      .recharts-responsive-container {
        outline: none !important;
        border: none !important;
      }

      .recharts-wrapper:focus,
      .recharts-surface:focus {
        outline: none !important;
      }

      .recharts-cartesian-grid-bg {
        fill: transparent !important;
        stroke: transparent !important;
      }

      .recharts-cartesian-grid rect {
        stroke: transparent !important;
      }

      /* Tooltip Customizado */
      .recharts-tooltip-wrapper {
        outline: none !important;
        border: none !important;
      }

      .custom-tooltip {
        background: ${({ theme }) => theme.card};
        border: none;
        outline: none;
        border-radius: 12px;
        padding: 16px;
        min-width: 200px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);

        .tooltip-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid ${({ theme }) => theme.border};

          .tooltip-month {
            font-size: 1em;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
          }

          .tooltip-index {
            font-size: 0.7em;
            font-weight: 600;
            color: ${({ theme }) => theme.primary};
            background: rgba(16, 185, 129, 0.1);
            padding: 3px 8px;
            border-radius: 6px;
          }
        }

        .tooltip-content {
          display: flex;
          flex-direction: column;
          gap: 10px;

          .tooltip-item {
            display: flex;
            align-items: center;
            gap: 8px;

            .tooltip-dot {
              width: 10px;
              height: 10px;
              border-radius: 3px;
              flex-shrink: 0;
            }

            .tooltip-label {
              flex: 1;
              font-size: 0.8em;
              color: ${({ theme }) => theme.text};
              opacity: 0.7;
            }

            .tooltip-value {
              font-size: 0.85em;
              font-weight: 700;
              color: ${({ theme }) => theme.text};
              font-family: "Inter", monospace;
            }
          }
        }

        .tooltip-footer {
          margin-top: 12px;
          padding-top: 10px;
          border-top: 1px dashed ${({ theme }) => theme.border};
          text-align: center;

          span {
            font-size: 0.7em;
            color: ${({ theme }) => theme.primary};
            font-weight: 500;
            opacity: 0.8;
          }
        }
      }
    }
  }

  /* ========== REFORMA TAB ========== */
  .reforma-tab {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
  }

  .cenario-buttons {
    display: flex;
    gap: 12px;
    margin-bottom: 12px;

    .cenario-btn {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      padding: 14px 28px;
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-left: 4px solid transparent;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.25s ease;

      &:hover {
        border-left-color: #3b82f6;
        background: rgba(59, 130, 246, 0.05);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
      }

      &.active {
        border-left-color: #3b82f6;
        background: rgba(59, 130, 246, 0.08);
        box-shadow: 0 4px 16px rgba(59, 130, 246, 0.15);

        .cenario-year {
          color: #3b82f6;
        }

        .cenario-label {
          opacity: 0.8;
        }
      }

      .cenario-year {
        font-size: 1.2em;
        font-weight: 700;
        color: ${({ theme }) => theme.text};
        font-family: 'Inter', system-ui, sans-serif;
        letter-spacing: -0.02em;
      }

      .cenario-label {
        font-size: 0.8em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        font-weight: 500;
      }
    }

    @media (max-width: 768px) {
      flex-wrap: wrap;

      .cenario-btn {
        flex: 1;
        min-width: 120px;
        padding: 12px 20px;
      }
    }
  }

  .reforma-search {
    margin-top: 8px;
    margin-bottom: 12px;

    .search-input-wrapper {
      position: relative;
      width: 100%;

      .search-icon {
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        font-size: 1.1em;
      }

      .search-input {
        width: 100%;
        padding: 14px 16px 14px 48px;
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 12px;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        transition: all 0.2s ease;

        &::placeholder {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
        }

        &:focus {
          outline: none;
          border-color: ${({ theme }) => theme.primary};
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
      }
    }
  }

  .reforma-content-row {
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 16px;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
    }
  }

  .reforma-products-table {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    overflow: hidden;

    .table-header {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
      gap: 12px;
      padding: 14px 20px;
      background: ${({ theme }) => theme.cardBg};
      border-bottom: 1px solid ${({ theme }) => theme.border};

      .header-cell {
        font-size: 0.75em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        text-align: center;

        &.produto {
          text-align: left;
        }
      }
    }

    .table-body {
      max-height: 600px;
      overflow-y: overlay;

      &::-webkit-scrollbar {
        width: 4px;
      }
      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.border};
        border-radius: 2px;
      }
    }

    .product-row {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
      gap: 12px;
      padding: 14px 20px;
      border-bottom: 1px solid ${({ theme }) => theme.border};
      border-left: 3px solid var(--product-color, #64748b);
      transition: background 0.2s ease;
      cursor: pointer;

      &:hover {
        background: ${({ theme }) => theme.tableRowHover};
      }

      &:last-child {
        border-bottom: none;
      }

      .product-info {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .product-code {
          font-size: 0.75em;
          color: var(--product-color, #64748b);
          font-family: "SF Mono", "Consolas", "Monaco", monospace;
          font-weight: 600;
        }

        .product-name {
          font-size: 0.85em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.9;
          line-height: 1.3;
        }
      }

      .tax-value {
        font-size: 0.85em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.8;
        text-align: center;
        font-family: 'Inter', system-ui, sans-serif;
      }
    }
  }

  .impacto-sidebar {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .impacto-main-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-left: 4px solid #ef4444;
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;

    .impacto-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;
    }

    .impacto-icon {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(239, 68, 68, 0.1);
      border-radius: 12px;
      color: #ef4444;
      font-size: 1.1em;
    }

    .impacto-badge {
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 0.65em;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;

      &.alto {
        background: rgba(239, 68, 68, 0.15);
        color: #ef4444;
      }

      &.medio {
        background: rgba(245, 158, 11, 0.15);
        color: #f59e0b;
      }

      &.baixo {
        background: rgba(16, 185, 129, 0.15);
        color: #10b981;
      }
    }

    .impacto-title {
      font-size: 0.8em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
    }

    .impacto-value {
      font-size: 1.3em;
      font-weight: 700;
      color: ${({ theme }) => theme.text};
      font-family: 'Inter', system-ui, sans-serif;
      letter-spacing: -0.02em;
    }

    .impacto-desc {
      font-size: 0.75em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
    }

    .impacto-variation {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 6px;
      padding-top: 10px;
      border-top: 1px solid ${({ theme }) => theme.border};

      svg {
        color: #ef4444;
        font-size: 1em;
      }

      span {
        font-size: 0.9em;
        font-weight: 700;
        color: #ef4444;
      }

      .variation-label {
        font-size: 0.75em;
        font-weight: 400;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
      }
    }
  }

  .impacto-compare-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 14px;

    .compare-title {
      font-size: 0.75em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      display: block;
      margin-bottom: 10px;
    }

    .compare-items {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .compare-item {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &.highlight {
        padding-top: 10px;
        margin-top: 4px;
        border-top: 1px solid ${({ theme }) => theme.border};
      }

      .compare-label {
        font-size: 0.75em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
      }

      .compare-value {
        font-size: 0.85em;
        font-weight: 600;
        font-family: 'Inter', system-ui, sans-serif;

        &.atual {
          color: ${({ theme }) => theme.text};
        }

        &.novo {
          color: #3b82f6;
        }

        &.diff {
          color: #ef4444;
        }
      }
    }
  }

  .impacto-aliquotas-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 14px;

    .aliquotas-title {
      font-size: 0.75em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      display: block;
      margin-bottom: 10px;
    }

    .aliquota-row {
      display: flex;
      gap: 10px;
      margin-bottom: 10px;
    }

    .aliquota-item {
      flex: 1;
      background: ${({ theme }) => theme.buttonBg};
      border-radius: 12px;
      padding: 10px;
      text-align: center;

      .aliquota-sigla {
        display: block;
        font-size: 0.7em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
        margin-bottom: 4px;
      }

      .aliquota-valor {
        font-size: 1.1em;
        font-weight: 700;
        color: #3b82f6;
        font-family: 'Inter', system-ui, sans-serif;
      }
    }

    .aliquota-total {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 12px;
      border-top: 1px solid ${({ theme }) => theme.border};

      span {
        font-size: 0.75em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
      }

      .total-valor {
        font-size: 1em;
        font-weight: 700;
        color: ${({ theme }) => theme.primary};
        opacity: 1;
        font-family: 'Inter', system-ui, sans-serif;
      }
    }
  }

  .reforma-charts-row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 16px;

    .reforma-chart-card {
      flex: 1;
    }

    @media (max-width: 1200px) {
      flex-direction: column;
    }
  }

  .reforma-chart-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;

    .chart-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 8px;

      .chart-title {
        display: flex;
        align-items: center;
        gap: 10px;

        .chart-icon {
          font-size: 1.2em;
          color: #3b82f6;
        }

        span {
          font-size: 0.95em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }
      }

      .chart-subtitle {
        font-size: 0.75em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
      }
    }

    .chart-container {
      flex: 1;
      min-height: auto;
      padding: 0;
    }

    .chart-legend {
      display: flex;
      justify-content: center;
      gap: 20px;
      margin-top: 0;
      padding-top: 8px;
      border-top: none;

      .legend-item {
        display: flex;
        align-items: center;
        gap: 8px;

        .legend-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }

        span {
          font-size: 0.8em;
          color: ${({ theme }) => theme.text};
          opacity: 0.7;
        }
      }
    }

    .chart-footer {
      display: flex;
      justify-content: space-around;
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid ${({ theme }) => theme.border};

      .footer-stat {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;

        .stat-label {
          font-size: 0.75em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
        }

        .stat-value {
          font-size: 1.1em;
          font-weight: 700;
          color: ${({ theme }) => theme.text};
          font-family: 'Inter', system-ui, sans-serif;

          &.increase {
            color: #ef4444;
          }

          &.decrease {
            color: #10b981;
          }
        }
      }
    }
  }

  .impacto-timeline-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 16px;

    .timeline-title {
      font-size: 0.75em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      display: block;
      margin-bottom: 16px;
    }

    .timeline-steps {
      display: flex;
      justify-content: space-between;
      position: relative;
      padding: 0 8px;

      &::before {
        content: "";
        position: absolute;
        top: 18px;
        left: 32px;
        right: 32px;
        height: 3px;
        background: linear-gradient(
          90deg,
          ${({ theme }) => theme.primary} 0%,
          ${({ theme }) => theme.border} 33%,
          ${({ theme }) => theme.border} 100%
        );
        border-radius: 2px;
      }
    }

    .timeline-step {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      position: relative;
      z-index: 1;

      .step-year {
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${({ theme }) => theme.buttonBg};
        border: 2px solid ${({ theme }) => theme.border};
        border-radius: 50%;
        font-size: 0.75em;
        font-weight: 700;
        color: ${({ theme }) => theme.text};
        transition: all 0.2s ease;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      .step-desc {
        font-size: 0.7em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
        font-weight: 500;
      }

      &.active {
        .step-year {
          background: ${({ theme }) => theme.primary};
          border-color: ${({ theme }) => theme.primary};
          color: #fff;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
          transform: scale(1.1);
        }

        .step-desc {
          opacity: 0.8;
          font-weight: 600;
        }
      }
    }
  }
`;

export const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 24px;
  align-items: start;

  .suppliers-table {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    overflow: hidden;

    .table-header {
      display: grid;
      grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1fr);
      gap: 16px;
      padding: 10px 16px;
      background: ${({ theme }) => surface(theme, 1)};
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};

      .header-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        font-size: 0.68em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        white-space: nowrap;
        ${mono};

        svg { font-size: 0.95em; opacity: 0.4; }

        &.fornecedor { justify-content: flex-start; }
      }
    }

    .table-body {
      display: flex;
      flex-direction: column;
      max-height: 600px;
      overflow-y: auto;

      &::-webkit-scrollbar { width: 4px; }
      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => hairline(theme, true)};
        border-radius: 2px;
      }
    }

  }

  .categories-card {
    height: 650px;
    position: sticky;
    top: 20px;
  }

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;

    .categories-card {
      position: static;
      height: 500px;
    }
  }
`;
